import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Card,
    FormControlLabel,
    Checkbox,
    InputAdornment
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { useNavigate } from 'react-router-dom';
import useDictation from '../../hooks/useDictation';
import Swal from 'sweetalert2';
import { URL, CONTINUOUS_IMPROVEMENT_STORE } from '../../config/constants';
import { initializeDB } from '../../config/db';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';

const DRAFT_KEY = 'ci_draft';

export default function ContinuousImprovementForm() {
    const navigate = useNavigate();

    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const descriptionRef = useRef('');

    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true
    });

    // Load draft on mount and set default employee
    useEffect(() => {
        loadDraft();

        // Set default employee from localStorage
        const fullName = localStorage.getItem('employeeFullName');
        const employeeId = localStorage.getItem('employeeId');
        if (fullName) {
            setSelectedEmployee({
                id: employeeId,
                FullName: fullName,
                _type: 'employee'
            });
        }
    }, []);

    const loadDraft = async () => {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(CONTINUOUS_IMPROVEMENT_STORE)) {
                const draft = await db.get(CONTINUOUS_IMPROVEMENT_STORE, DRAFT_KEY);
                if (draft?.description) {
                    setDescription(draft.description);
                    descriptionRef.current = draft.description;
                }
                if (draft?.isAnonymous !== undefined) {
                    setIsAnonymous(draft.isAnonymous);
                }
            }
        } catch (e) {
            console.error('Error loading draft:', e);
        }
    };

    const saveDraft = async (text, anonymous = isAnonymous) => {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(CONTINUOUS_IMPROVEMENT_STORE)) {
                await db.put(CONTINUOUS_IMPROVEMENT_STORE, {
                    id: DRAFT_KEY,
                    description: text,
                    isAnonymous: anonymous,
                    updatedAt: new Date().toISOString()
                });
            }
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    const clearDraft = async () => {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(CONTINUOUS_IMPROVEMENT_STORE)) {
                await db.delete(CONTINUOUS_IMPROVEMENT_STORE, DRAFT_KEY);
            }
        } catch (e) {
            console.error('Error clearing draft:', e);
        }
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        setDescription(value);
        descriptionRef.current = value;
        saveDraft(value);
    };

    const handleAnonymousChange = (e) => {
        const checked = e.target.checked;
        setIsAnonymous(checked);
        saveDraft(description, checked);
    };

    const handleEmployeeSelect = (selectedArray) => {
        if (selectedArray && selectedArray.length > 0) {
            setSelectedEmployee(selectedArray[0]);
        }
        setEmployeeModalOpen(false);
    };

    const toggleDictation = async () => {
        if (!dictationSupported) {
            return;
        }

        if (isListening) {
            stopDictation();
        } else {
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    const currentText = descriptionRef.current || '';
                    const newText = currentText + (currentText ? ' ' : '') + transcript;
                    setDescription(newText);
                    descriptionRef.current = newText;
                    saveDraft(newText);
                },
                onError: (e) => {
                    console.error('Recognition error:', e?.error || e);
                }
            });
        }
    };

    const handleSubmit = async () => {
        if (!description.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Empty Suggestion',
                text: 'Please enter your improvement suggestion'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Suggestion?',
            text: isAnonymous
                ? 'Your suggestion will be submitted anonymously'
                : 'Your improvement idea will be sent for review',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4caf50',
            cancelButtonColor: '#9e9e9e',
            confirmButtonText: 'Yes, submit'
        });

        if (!result.isConfirmed) return;

        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');

            const response = await fetch(`${URL}/continuous-improvement/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    description: description.trim(),
                    isAnonymous: isAnonymous,
                    employeeId: selectedEmployee?.EE || selectedEmployee?.ee || selectedEmployee?.id || null,
                    employeeName: selectedEmployee?.FullName || null
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to submit');
            }

            await clearDraft();

            Swal.fire({
                icon: 'success',
                title: 'Thank You!',
                text: 'Your suggestion has been submitted'
            });

            navigate('/main');
        } catch (error) {
            console.error('Error submitting:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to submit suggestion'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                px: 1.5,
                py: 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
            }}>
                <IconButton onClick={() => navigate('/main')} edge="start">
                    <ArrowBackIcon />
                </IconButton>
                <TipsAndUpdatesIcon sx={{ color: '#ff9800', ml: 1 }} />
                <Typography variant="h6" fontWeight={600} sx={{ ml: 1 }}>
                    Submit Idea
                </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto' }}>
                {/* Submitter row */}
                <Card sx={{ p: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                            From:
                        </Typography>
                        <TextField
                            size="small"
                            value={isAnonymous ? 'Anonymous' : (selectedEmployee?.FullName || '')}
                            placeholder="Select employee"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <PersonIcon fontSize="small" sx={{ color: '#607d8b' }} />
                                    </InputAdornment>
                                )
                            }}
                            onClick={!isAnonymous ? () => setEmployeeModalOpen(true) : undefined}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    cursor: isAnonymous ? 'default' : 'pointer',
                                    backgroundColor: isAnonymous ? '#f5f5f5' : '#fff'
                                }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAnonymous}
                                    onChange={handleAnonymousChange}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Anonymous</Typography>}
                            sx={{ m: 0 }}
                        />
                    </Box>
                </Card>

                {/* Description field */}
                <Card sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Your Suggestion
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        placeholder="Describe your idea for improvement..."
                        value={description}
                        onChange={handleDescriptionChange}
                        InputProps={{
                            endAdornment: dictationSupported && (
                                <IconButton
                                    onClick={toggleDictation}
                                    size="small"
                                    color={isListening ? 'error' : 'default'}
                                >
                                    {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                </IconButton>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fafafa'
                            }
                        }}
                    />
                </Card>
            </Box>

            {/* Submit Button */}
            <Box sx={{
                backgroundColor: 'white',
                borderTop: '1px solid #e0e0e0',
                p: 1.5,
                flexShrink: 0
            }}>
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={submitting || !description.trim()}
                    sx={{
                        backgroundColor: '#4caf50',
                        '&:hover': { backgroundColor: '#43a047' },
                        '&:disabled': { backgroundColor: '#c8e6c9' },
                        py: 1.5
                    }}
                >
                    {submitting ? 'Submitting...' : 'Submit Suggestion'}
                </Button>
            </Box>

            {/* Employee Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleEmployeeSelect}
                singleSelect={true}
                employeesOnly={true}
                alreadySelected={selectedEmployee ? [selectedEmployee] : []}
            />
        </Box>
    );
}
